import type Rollbar from 'rollbar'

/**
 * Drops occurrences whose stack lives entirely inside a browser extension.
 *
 * An extension's injected content script throws in the page, and because it
 * runs in our window the error reaches Rollbar through our own
 * `captureUncaught` handler. The stack frames name the extension bundle rather
 * than anything we ship, e.g. CE-10621:
 *
 *   TypeError: Cannot read properties of undefined (reading 'M_ID')
 *     chrome-extension://eppiocemhmnlbhjplcgkofciiegomcon/executors/200.js:1 in Y
 *     chrome-extension://eppiocemhmnlbhjplcgkofciiegomcon/executors/200.js:1 in E
 *
 * `ignoredMessages` is the wrong tool for these: the message is an ordinary
 * `TypeError` that our own code could legitimately produce, so the only thing
 * that distinguishes them is where the frames come from.
 *
 * Rollbar also ships `hostBlockList`, but it drops an occurrence when *any*
 * frame matches, which would hide a real application error that merely passed
 * through an extension's monkey-patched listener on its way up. This is the
 * conservative version: ignore only when every frame we can attribute belongs
 * to an extension, so a stack with even one of our own frames still reports.
 */

const EXTENSION_URL =
  /^(?:chrome-extension|moz-extension|safari-web-extension|safari-extension|ms-browser-extension|extension):\/\//i

interface RollbarFrame {
  filename?: unknown
}

interface RollbarTrace {
  frames?: RollbarFrame[]
}

interface RollbarBody {
  trace?: RollbarTrace
  trace_chain?: RollbarTrace[]
}

/**
 * Predicates run after `itemToPayload`, so the argument is the payload itself:
 * a single-exception occurrence carries `body.trace`, and one with a `cause`
 * chain carries `body.trace_chain` instead.
 */
const attributedFrames = (item: Rollbar.Dictionary): RollbarFrame[] => {
  const body = (item as { body?: RollbarBody })?.body
  if (!body) return []

  const traces = Array.isArray(body.trace_chain) ? body.trace_chain : [body.trace]

  return traces
    .flatMap((trace) => (Array.isArray(trace?.frames) ? trace.frames : []))
    .filter((frame) => typeof frame?.filename === 'string')
}

const isExtensionFrame = (frame: RollbarFrame): boolean =>
  EXTENSION_URL.test(frame.filename as string)

const rollbarCheckIgnore: NonNullable<Rollbar.Configuration['checkIgnore']> = (
  _isUncaught,
  _args,
  item,
) => {
  const frames = attributedFrames(item)

  return frames.length > 0 && frames.every(isExtensionFrame)
}

export default rollbarCheckIgnore
